/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {environment} from '../../environments/environment';
import { RestResponse, User, Notification} from '../model';
import {SearchableProvider} from '../model/interface/searchable.provider';
import {ApiHeaders} from './headers';

@Injectable()
export class UserProvider implements SearchableProvider {

  constructor(
    private http: HttpClient
  ) {}

  getMyProfile(): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/user`, ApiHeaders.getOptions());
  }

  changePassword(password: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/user/password`, {FIELD: 'PASSWORD', VALUE: password}, ApiHeaders.getOptions());
  }

  search(query?: string, id?: string, nonBank?: boolean, lsp?: boolean, page?: number, position?: string): Observable<RestResponse> {
    return this.getUsers(page ? page : 1, query, position);
  }

  getUsers(page: number, query?: string, position?: string): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/user/list?page=${page}${query ? '&query=' + encodeURIComponent(query) : ''}${position ? '&position=' + encodeURIComponent(position) : ''}`,
        ApiHeaders.getOptions());
  }

  getUser(id: string): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/user/${id}`, ApiHeaders.getOptions());
  }

  saveUser(user: User): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/user/`, user, ApiHeaders.getOptions());
  }

  saveUserAvatar(avatar: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/user/avatar`, {image: avatar}, ApiHeaders.getOptions());
  }
  deleteUserAvatar(): Observable<RestResponse> {
    return this.http
      .delete<RestResponse>(`${environment.API_URL}/api/user/avatar`, ApiHeaders.getOptions());
  }

  deleteUser(user: User): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/user/${user.ID}/delete`, null, ApiHeaders.getOptions());
  }

  enableUser(user: User): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/user/${user.ID}/enable`, null, ApiHeaders.getOptions());
  }

  disableUser(user: User): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/user/${user.ID}/disable`, null, ApiHeaders.getOptions());
  }

  inviteUser(email: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/user/invite`,
        {FIELD: 'EMAIL', VALUE: email}, ApiHeaders.getOptions());
  }

  addRole(userId: string, roleId: string): Observable<RestResponse> {
    return this.http
      .put<RestResponse>(`${environment.API_URL}/api/user/${userId}/role/${roleId}`, null, ApiHeaders.getOptions());
  }

  removeRole(userId: string, roleId: string): Observable<RestResponse> {
    return this.http
      .delete<RestResponse>(`${environment.API_URL}/api/user/${userId}/role/${roleId}`, ApiHeaders.getOptions());
  }

  addPosition(userId: string, positionId: string): Observable<RestResponse> {
    return this.http
      .put<RestResponse>(`${environment.API_URL}/api/user/${userId}/position/${positionId}`, null, ApiHeaders.getOptions());
  }

  removePosition(userId: string, positionId: string): Observable<RestResponse> {
    return this.http
      .delete<RestResponse>(`${environment.API_URL}/api/user/${userId}/position/${positionId}`, ApiHeaders.getOptions());
  }


  save(id: string, field: string, value: any): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/user/${id}/field`, {
        FIELD: field,
        VALUE: value
      }, ApiHeaders.getOptions());
  }

  // ===================== Notifications ===================== //

  getNotifications(): Observable<RestResponse> {
    return this.http
      .get<RestResponse>(`${environment.API_URL}/api/user/notification`, ApiHeaders.getOptions());
  }

  addNotificationToMe(id: string): Observable<RestResponse> {
    return this.http
      .put<RestResponse>(`${environment.API_URL}/api/user/notification/${id}`, null, ApiHeaders.getOptions());
  }

  addMultipleNotificationsToMe(notifications: Notification[]): Observable<RestResponse> {
    return this.http
      .put<RestResponse>(`${environment.API_URL}/api/user/notifications/add`, notifications, ApiHeaders.getOptions());
  }

  removeNotificationToMe(id: string): Observable<RestResponse> {
    return this.http
      .delete<RestResponse>(`${environment.API_URL}/api/user/notification/${id}`, ApiHeaders.getOptions());
  }

  removeMultipleNotificationsToMe(notifications: Notification[]): Observable<RestResponse> {
    return this.http
      .put<RestResponse>(`${environment.API_URL}/api/user/notifications/remove`, notifications, ApiHeaders.getOptions());
  }

  addMyNotification(id: string): Observable<RestResponse>{
    return this.http
      .put<RestResponse>(`${environment.API_URL}/api/user/notification?id=` + id, null, ApiHeaders.getOptions());
  }

  removeMyNotification(id: string): Observable<RestResponse>{
    return this.http
      .delete<RestResponse>(`${environment.API_URL}/api/user/notification?id=` + id, ApiHeaders.getOptions());
  }

  setUpTfa(): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/user/tfa`, ApiHeaders.getOptions());
  }

  verifyCode(code: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/user/tfa/verify`,
      {
        FIELD: 'CODE',
        VALUE: code
      }, ApiHeaders.getOptions());
  }

  disableTfa(): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/user/tfa/disable`, ApiHeaders.getOptions());
  }

  getSearchType(): string {
    return 'USER';
  }
}
